import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import CustomDropdown from '@components/CustomDropdown';
import ModalContainer from '@components/modals/ModalContainer';
import { toastError, toastSuccess } from '@components/Toast';

import { locationInfo } from '@features/design/utils/designUtils';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { submitLocationUpdate } from '@services/commonService';
import { getProductSampleLocations, getSampleCountryList } from '@services/designInfoUpdateService';

import { printApiError } from '@utils/Util';

const UpdateSampleLocation = ({
	setIsShowUpdateLocationModal,
	selectedProductIds,
	isShowCountry = false,
	isOnTheWay = false,
	onSubmit
}) => {
	const [countryList, setCountryList] = useState([]);
	const [locationList, setLocationList] = useState([]);
	const [selectedCountry, setSelectedCountry] = useState(null);
	const [selectedLocation, setSelectedLocation] = useState(null);
	const [remarks, setRemarks] = useState('');
	const dispatch = useDispatch();

	const fetchCountryList = async () => {
		try {
			dispatch(openLoader());
			const response = await getSampleCountryList();
			const countryList = response.map((country) => ({
				...country,
				value: country.code
			}));
			setCountryList(countryList);
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	const getSampleLocations = async (searchKey = '') => {
		try {
			let params = `?search=${searchKey}&page=0&size=20`;
			params += isOnTheWay ? `&isOnTheWay=${isOnTheWay}` : '';

			dispatch(openLoader());
			const response = await getProductSampleLocations(params);
			const locationsArr = response?.map((item) => ({
				...item,
				secondLine: [
					item?.countryResponse?.code,
					item?.buildingTagResponse?.text,
					item?.floorTagResponse?.text,
					item?.roomOrAreaTagResponse?.text,
					item?.otherDetails
				]
					.filter(Boolean)
					.join(', ')
			}));

			setLocationList(locationsArr);
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	const handleSearchLocation = (value) => {
		if (value && value.trim()) {
			const filteredLocations = locationList.filter((location) => {
				const secondLine = location?.secondLine?.toLowerCase().includes(value.toLowerCase());
				const locationId = location?.locationId?.toLowerCase().includes(value.toLowerCase());
				return secondLine || locationId;
			});
			setLocationList(filteredLocations);
		} else {
			getSampleLocations();
		}
	};

	useEffect(() => {
		if (isShowCountry) {
			fetchCountryList();
		}
		getSampleLocations();
	}, [isShowCountry]);

	const handleReset = () => {
		setSelectedCountry(null);
		setSelectedLocation(null);
	};

	const handleSubmitLocation = async () => {
		if (isShowCountry && !selectedCountry?.id) {
			toastError('Please Select Country!');
			return;
		}
		if (!selectedLocation?.id) {
			toastError('Please Select Location!');
			return;
		}
		try {
			dispatch(openLoader());
			let params = `/${selectedLocation?.id}?productIds=${selectedProductIds.join(',')}`;

			params += isOnTheWay ? `&isOnTheWay=${isOnTheWay}` : '';

			const response = await (onSubmit
				? onSubmit(selectedLocation, remarks)
				: submitLocationUpdate(params));
			if (response?.data?.success) {
				handleReset();
				toastSuccess('Location Updated!');
				setIsShowUpdateLocationModal(false);
			}
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	return (
		<ModalContainer
			onCloseModal={() => setIsShowUpdateLocationModal(false)}
			className="sample-location-modal"
		>
			<div className="wraper container-fluid dashboard-container">
				<div className="popup-wrapper">
					<div className="common-popup sample-location-modal">
						<div className="common-popup-header d-flex justify-content-between border-0">
							<div className="popup-tilte">
								<h3 className="semibold-16 mb-0">Update sample location</h3>
							</div>
							<div className="close-btn" onClick={() => setIsShowUpdateLocationModal(false)}>
								<img src={`${process.env.PUBLIC_URL}/icons/close.svg`} alt="close" />
							</div>
						</div>
						<div className="common-popup-body py-0">
							<div className="row">
								{isShowCountry && (
									<div className="col-md-12">
										<div className="form-group">
											<div className="select-component">
												<label htmlFor="collection">Sample ID country*</label>
												<CustomDropdown
													title="Select country"
													items={countryList}
													selectedItem={selectedCountry?.code}
													onItemClick={(value) => setSelectedCountry(value)}
												/>
											</div>
										</div>
									</div>
								)}
								<div className="col-md-12">
									<div className="form-group">
										<div className="select-component">
											<label htmlFor="collection">Location</label>
											<CustomDropdown
												title="Select location"
												items={locationList}
												selectedItem={locationInfo(selectedLocation)}
												onItemClick={(value) => setSelectedLocation(value)}
												onItemSearch={(value) => handleSearchLocation(value)}
											/>
										</div>
									</div>
								</div>
								<div className="col-md-12">
									<div className="form-group">
										<label htmlFor="collection">Remarks</label>
										<input
											type="text"
											placeholder="Brand name, Customer or concern person"
											value={remarks}
											onChange={(e) => setRemarks(e.target.value)}
										/>
									</div>
								</div>
							</div>
						</div>
						<div className="modal-footer">
							<div className="footer-buttons text-right">
								<button
									className="button gray-outline size32"
									onClick={() => {
										handleReset();
										setIsShowUpdateLocationModal(false);
									}}
								>
									Cancel
								</button>
								<button className="button size32 ml-2" onClick={() => handleSubmitLocation()}>
									Submit
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</ModalContainer>
	);
};

export default UpdateSampleLocation;
